import {
  Breadcrumbs,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";

import Navigation from "../Navigation";
import BAACard from "./BAAUploader/BAACard";
import BAAUploadModal from "./BAAUploader/BAAUploadModal";
import BlockerStalenessBanner from "./BlockerStalenessBanner";
import DownloadLatestReportButton from "./DownloadLatestReportButton";
import useFetchLatestReport from "./queries/useFetchLatestReport";
import Risk from "./Risk";
import {
  effectiveRiskLevel,
  firstFoundDomain,
  LatestWebTrackerReportResponse,
} from "./types";
import { WebTrackerFollowUpTask } from "./WebTrackerDetailsPage/FollowUpTasks/FollowUpTasksTypes";
import DomainsList from "./WebTrackerDetailsPage/DomainsList";
import ExamplePagesTable from "./WebTrackerDetailsPage/ExamplePagesTable";
import FollowUpTaskDetailsModal from "./WebTrackerDetailsPage/FollowUpTasks/FollowUpTaskDetailsModal";
import FollowUpTasks from "./WebTrackerDetailsPage/FollowUpTasks/FollowUpTasks";
import TrackerNotes from "./WebTrackerDetailsPage/TrackerNotes";
import TrackerResolution from "./WebTrackerDetailsPage/TrackerResolution";
import useWTMURLs from "./useWTMURLs";

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  breadcrumbLink: {
    cursor: "pointer",
  },
  columnMainContent: {
    padding: 0,
    position: "relative",
    width: "100%",
  },
  columnMainContentInner: {
    padding: "1em",
  },
  columnSidebar: {
    padding: 0,
    background: "transparent",
    border: "none",
    outline: "none",
    "& > div": {
      marginBottom: theme.spacing(3),
    },
  },
  darkText: {
    color: "#263238",
  },
  description: {
    marginBottom: "1em",
    marginTop: "1em",
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
  tableNav: {
    padding: "1em 0",
  },
}));

function WebTrackerDetailsPageHelper({
  latestReport,
  trackerName,
}: {
  latestReport: LatestWebTrackerReportResponse;
  trackerName: string;
}) {
  const classes = useStyles();

  const webTracker = latestReport.trackers.find(
    (tracker) => tracker.name === trackerName
  );

  const webTrackerStats = latestReport.reportTrackerStats.find(
    (stats) => stats.tracker === trackerName
  );

  useEffect(() => {
    if (webTracker === undefined) {
      return;
    }
  }, [webTracker]);

  const [isBAAModalOpen, setIsBAAModalOpen] = useState(false);

  const [
    followUpTaskForModal,
    setFollowUpTaskForModal,
  ] = useState<WebTrackerFollowUpTask | null>(null);
  const [
    isFollowUpTaskDetailsModalOpen,
    setFollowUpTaskDetailsModalOpen,
  ] = useState(false);

  const [isResolutionLoading, setIsResolutionLoading] = useState(false);

  if (webTracker === undefined || webTrackerStats === undefined) {
    return null;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={8}>
        <Paper className={classes.columnMainContent}>
          <div className={classes.columnMainContentInner}>
            <Risk
              description={webTracker.description}
              riskLevel={effectiveRiskLevel(webTracker)}
            />

            <Typography
              component="h2"
              variant="h2"
              style={{ marginTop: "1em" }}
            >
              {webTracker.name}
            </Typography>

            <div className={clsx(classes.description)}>
              <Typography component="p" variant="body1">
                {webTracker.description}
              </Typography>
            </div>

            <DomainsList domains={webTracker.domains} />
          </div>

          {isBAAModalOpen && (
            <BAAUploadModal
              onClose={() => setIsBAAModalOpen(false)}
              siteDomain={latestReport.report.domain}
              trackerDomain={firstFoundDomain(webTrackerStats)}
              trackerName={webTracker.name}
              resolution={webTracker.resolution}
              setIsResolutionLoading={setIsResolutionLoading}
            />
          )}

          {isFollowUpTaskDetailsModalOpen && (
            <FollowUpTaskDetailsModal
              onClose={() => setFollowUpTaskDetailsModalOpen(false)}
              siteDomain={latestReport.report.domain}
              trackerDomain={firstFoundDomain(webTrackerStats)}
              webTracker={webTracker}
              followUpTask={followUpTaskForModal}
            />
          )}

          <Divider />

          <nav className={clsx(classes.columnMainContentInner)}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Grid item xs={9}>
                <Typography component="p" variant="body1">
                  <strong>
                    Found on
                    {webTrackerStats.approxNumPages === 0
                      ? " "
                      : " approximately "}
                    {webTrackerStats.approxNumPages} pages.
                  </strong>{" "}
                  {webTrackerStats.approxNumPages > 0 &&
                    `Download the report to see the full list.`}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <DownloadLatestReportButton />
              </Grid>
            </Grid>
          </nav>

          <ExamplePagesTable webTrackerStats={webTrackerStats} />
        </Paper>
      </Grid>
      <Grid
        className={classes.columnSidebar}
        component={(props) => (
          <Paper
            {...props}
            direction="column"
            elevation={0}
            variant="elevation"
          />
        )}
        alignItems="stretch"
        direction="column"
        item
        container
        spacing={0}
        xs={4}
      >
        <Grid item>
          <TrackerResolution
            webTracker={webTracker}
            webTrackerReportStats={webTrackerStats}
            disableBAA={webTracker.supportsBAA === "no"}
            isResolutionLoading={isResolutionLoading}
          />
        </Grid>

        <Grid item>
          <FollowUpTasks
            webTracker={webTracker}
            openFollowUpTaskDetailsModal={() =>
              setFollowUpTaskDetailsModalOpen(true)
            }
            setFollowUpTaskForModal={setFollowUpTaskForModal}
            onClickBAAUpload={() => setIsBAAModalOpen(true)}
          />
        </Grid>

        <Grid item>
          <TrackerNotes webTrackerReportStats={webTrackerStats} />
        </Grid>

        <Grid item>
          <BAACard
            onClickUpload={() => setIsBAAModalOpen(true)}
            siteDomain={latestReport.report.domain}
            trackerDomain={firstFoundDomain(webTrackerStats)}
            supportsBAA={webTracker.supportsBAA}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

function WebTrackerDetailsPage() {
  const classes = useStyles();
  const { trackerId: trackerName } = useParams<Record<string, string>>();
  const wtmURLs = useWTMURLs();
  const { isLoading, isError, data: latestReport } = useFetchLatestReport();

  let contents: JSX.Element | string | null;
  if (isError) {
    contents =
      "Something went wrong. Try refreshing the page, or contact support@freshpaint.io for assistance.";
  } else if (isLoading) {
    contents = (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  } else if (latestReport === undefined) {
    // in this case, the current environment is not setup for web tracker
    // monitoring. redirect to trackers overview where the empty state will clarify
    contents = <Redirect to={wtmURLs.trackersOverview} />;
  } else {
    contents = (
      <WebTrackerDetailsPageHelper
        latestReport={latestReport}
        trackerName={trackerName}
      />
    );
  }

  return (
    <Navigation className={clsx(classes.navigation)}>
      <BlockerStalenessBanner />
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Typography variant="overline">Monitoring</Typography>
          <Link
            className={classes.breadcrumbLink}
            to={wtmURLs.trackersOverview}
          >
            <Typography variant="overline">Tracking Tools</Typography>
          </Link>
          <Typography className={clsx(classes.darkText)} variant="overline">
            {trackerName.toUpperCase()}
          </Typography>
        </Breadcrumbs>
      </div>
      {contents}
    </Navigation>
  );
}

export default WebTrackerDetailsPage;
